import { useLegalClient } from 'api/requests';
import { useCountries } from 'api/requests/country';
import { useLegalClientRepresentative as useLegalClientRepresentativeApi } from 'api/requests/legalRepresentative';
import { NavigationBreadcrumbsTitles } from 'components';
import { parseFullName } from 'helpers';
import { FormSubmit } from 'libs/form/Form';
import { useTranslation } from 'libs/i18n';
import { useNavigate, RoutesId } from 'libs/navigation';
import { isLegalRepresentativeCompany } from 'modules/client/common/helpers';

import { LegalRepresentativeFormType } from '../forms/legalRepresentativeForm';
import { getLegalRepresentativeInitialValue } from '../helpers';

import { useLegalClientRepresentativeActions } from './useLegalClientRepresentativeActions';
import { useLegalPageTitle } from './useLegalPageTitle';
import { useLockLegalClientRepresentative } from './useLockLegalClientRepresentative';
import { useUpdateLegalClientRepresentative } from './useUpdateLegalClientRepresentative';

type LegalRepresentativeContainerParams = {
  legalClientRepresentativeId: string;
  legalClientId: string;
};

export const useLegalClientRepresentative = () => {
  const { params } = useNavigate<LegalRepresentativeContainerParams>();

  const { t } = useTranslation();

  const {
    legalClientRepresentative,
    error,
    loading: legalClientRepresentativeLoading,
    refetch,
  } = useLegalClientRepresentativeApi(params.legalClientRepresentativeId!);

  const { legalClient, loading: legalClientLoading } = useLegalClient(
    params.legalClientId!
  );

  const { loading: submitLoading, updateLegalClientRepresentative } =
    useUpdateLegalClientRepresentative(legalClient);

  const {
    onChangeClientType,
    onRevokeOtpVerification,
    onSendInvitationToAccount,
    onShowVersionHistory,
    revokeOtpVerificationLoading,
  } = useLegalClientRepresentativeActions({
    legalClient,
    legalClientRepresentative,
    refetch,
  });

  const { lockClientIfPossible, unlockClient } =
    useLockLegalClientRepresentative(legalClient, legalClientRepresentative);

  const { countries, loading: countriesLoading } = useCountries();

  const loading =
    (!legalClientRepresentative || !legalClient) &&
    (legalClientRepresentativeLoading || legalClientLoading);

  const onSubmit: FormSubmit<LegalRepresentativeFormType> = async (
    values,
    helpers
  ) => {
    if (
      await updateLegalClientRepresentative(legalClientRepresentative!, values)
    ) {
      helpers.setReadOnly(true);
    }
  };

  const initialValues = legalClientRepresentative
    ? getLegalRepresentativeInitialValue(t, legalClientRepresentative)
    : undefined;

  const isCompany = isLegalRepresentativeCompany(legalClientRepresentative);

  const breadcrumbsTitles: NavigationBreadcrumbsTitles = {
    [RoutesId.legalRepresentative]: legalClientRepresentative
      ? t('client.legalRepresentative.title', {
          name: isCompany
            ? legalClientRepresentative.company?.fullName
            : parseFullName(legalClientRepresentative),
        })
      : undefined,
    [RoutesId.legalClient]: legalClient?.fullName,
  };

  useLegalPageTitle(legalClient, legalClientRepresentative);

  return {
    legalClientRepresentative,
    loading,
    countries: countries ?? [],
    countriesLoading,
    initialValues,
    submitLoading,
    error,
    breadcrumbsTitles,
    legalClient,
    revokeOtpVerificationLoading,
    onChangeClientType,
    onSubmit,
    lockClientIfPossible,
    unlockClient,
    onShowVersionHistory,
    onRevokeOtpVerification,
    onSendInvitationToAccount,
  };
};
